/**
 * The week on a phone: one lane per day.
 *
 * The desktop grid is people down the side and days across the top, which is
 * the right shape for a laptop and the wrong shape for a phone, where it
 * becomes seven skinny columns or a sideways scroll. This view turns the week
 * on its side. Each DAY is a full-width lane, time runs left to right, and
 * every shift is drawn at its real hours as a bar carrying the person's
 * colour, initial and first name. Seven lanes fit one screen, so the whole
 * week stays in view, which is the one property a boss rostering on a phone
 * will not give up.
 *
 * What this view does not do is fetch or mutate anything. It draws the cached
 * week, and every change goes back through the roster page's mutations, so
 * the optimistic patching and the undo behave the same as the grid.
 *
 * Interaction, all thumb-sized:
 *   - tap the day or the wage to open that day; the other six shrink to strips
 *     but stay on screen
 *   - tap a bar to edit it
 *   - hold a bar (320ms) to pick it up. Movement locks to one axis after a
 *     few pixels: up and down changes the day, left and right nudges the start
 *     in 15 minute steps, and a floating tag shows the new time and what it
 *     does to the wage bill before the thumb lifts
 *   - tap empty lane space on the open day to add a shift there
 *   - the strip under each lane is coverage between the day's first start and
 *     last finish. Coral stripes mean nobody is on; tapping them offers a
 *     shift that fills the hole exactly
 */
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Hand, Lock } from 'lucide-react';
import type { Area, RosterTotals, Shift, StaffMember } from '../lib/api.ts';
import {
  DAY_SHORT,
  dayIndex,
  fmtMoneyShort,
  fmtTime12,
  shiftMinutes,
  today,
  type Hhmm,
  type IsoDate,
} from '../lib/time.ts';
import { personColours, TweenMoney } from '../components/Bits.tsx';
import { prefersReducedMotion } from '../lib/motion.ts';
import { bucketForDate, type Rates, type UnavailableIndex } from './rosterMath.ts';

export interface LaneMove {
  date: IsoDate;
  startTime: Hhmm;
  endTime: Hhmm;
}

interface Props {
  weekDates: IsoDate[];
  shifts: Shift[];
  staff: StaffMember[];
  areas: Area[];
  rates: Record<string, Rates>;
  totals: RosterTotals;
  unavailable: UnavailableIndex;
  onOpenShift: (id: string) => void;
  onAddShift: (date: IsoDate, times?: { startTime: Hhmm; endTime: Hhmm }) => void;
  onMoveShift: (id: string, to: LaneMove) => void;
}

const toMin = (t: Hhmm) => {
  const [h, m] = t.split(':').map(Number);
  return (h ?? 0) * 60 + (m ?? 0);
};
const toHhmm = (m: number): Hhmm =>
  `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`;
/** End minute for drawing. An overnight shift is clamped to midnight rather
 *  than drawn backwards; the pay maths handle the wrap, the picture need not. */
const endMin = (s: Shift) => {
  const e = toMin(s.endTime.slice(0, 5));
  const b = toMin(s.startTime.slice(0, 5));
  return e <= b ? 24 * 60 : e;
};
const startMin = (s: Shift) => toMin(s.startTime.slice(0, 5));

const HOLD_MS = 320;
const AXIS_LOCK_PX = 12;
const STEP_MIN = 15;

/** Greedy packing into sub-rows so concurrent shifts sit under one another. */
function packRows(list: Shift[]): Shift[][] {
  const rows: Shift[][] = [];
  for (const s of [...list].sort((a, b) => startMin(a) - startMin(b) || endMin(a) - endMin(b))) {
    const row = rows.find((r) => {
      const last = r[r.length - 1];
      return !!last && endMin(last) <= startMin(s);
    });
    if (row) row.push(s);
    else rows.push([s]);
  }
  return rows;
}

interface CoverSeg {
  s: number;
  e: number;
  level: 0 | 1 | 2 | 3;
}

/**
 * Headcount across the day in 15 minute slots, from the first start to the
 * last finish. A shift that needs cover does not count: the person on it has
 * said they cannot work it, so the floor should read as if they are not there.
 */
function coverage(list: Shift[]): CoverSeg[] {
  const counted = list.filter((s) => s.userId && !s.needsCover);
  if (list.length === 0) return [];
  const from = Math.min(...list.map(startMin));
  const to = Math.max(...list.map(endMin));
  const segs: CoverSeg[] = [];
  let cur: CoverSeg | null = null;
  for (let t = from; t < to; t += STEP_MIN) {
    const n = counted.filter((s) => startMin(s) <= t && endMin(s) > t).length;
    const level = Math.min(n, 3) as CoverSeg['level'];
    if (cur && cur.level === level) cur.e = t + STEP_MIN;
    else {
      cur = { s: t, e: t + STEP_MIN, level };
      segs.push(cur);
    }
  }
  return segs;
}

export function WeekLanes({
  weekDates,
  shifts,
  staff,
  areas,
  rates,
  totals,
  unavailable,
  onOpenShift,
  onAddShift,
  onMoveShift,
}: Props) {
  const [open, setOpen] = useState<IsoDate | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const todayIso = today();

  const people = useMemo(() => new Map(staff.map((s) => [s.userId, s])), [staff]);
  const colours = useMemo(() => personColours(staff), [staff]);
  const areaName = useMemo(() => new Map(areas.map((a) => [a.id, a.name])), [areas]);

  // The axis is cut to the week rather than fixed. Six to six would spend a
  // third of every lane on hours nobody works; the cafe's real span plus a
  // little margin lets the bars be as wide as they can be.
  const axis = useMemo(() => {
    const lo = Math.min(7 * 60, ...shifts.map(startMin));
    const hi = Math.max(16 * 60, ...shifts.map(endMin));
    const a0 = Math.max(0, Math.floor((lo - 30) / 30) * 30);
    const a1 = Math.min(24 * 60, Math.ceil((hi + 30) / 30) * 30);
    return { a0, a1, span: a1 - a0 };
  }, [shifts]);
  const pct = useCallback((m: number) => ((m - axis.a0) / axis.span) * 100, [axis]);

  const byDate = useMemo(() => {
    const map = new Map<IsoDate, Shift[]>();
    for (const d of weekDates) map.set(d, []);
    for (const s of shifts) map.get(s.date)?.push(s);
    return map;
  }, [shifts, weekDates]);

  // ---- row height: fit seven lanes to whatever height the phone gives ------
  // A phone browser can hand over 440px or 700px depending on its chrome and
  // whether the app is installed. Rows are sized for the TYPICAL day (the
  // median row count), so one unusually busy day grows its own lane rather
  // than shrinking the other six to match it. Rows stop at 11px; below that
  // the lanes scroll rather than become unreadable.
  const rowsTypical = useMemo(() => {
    const counts = weekDates.map((d) => packRows(byDate.get(d) ?? []).length).sort((a, b) => a - b);
    return Math.max(1, counts[Math.floor(counts.length / 2)] ?? 1);
  }, [weekDates, byDate]);
  useLayoutEffect(() => {
    const root = rootRef.current;
    const lanes = root?.querySelector<HTMLElement>('.wl-lanes');
    if (!root || !lanes) return;
    const fit = () => {
      const avail = lanes.clientHeight - 12 - 6 * 5; // padding, then the gaps between lanes
      const perLane = avail / 7;
      const fixed = 5 + 6 + 4 + 5 + 2; // track padding, coverage strip and its gap, borders
      const row = (perLane - fixed - (rowsTypical - 1) * 2) / rowsTypical;
      root.style.setProperty('--wl-row', `${Math.max(11, Math.min(17, Math.floor(row)))}px`);
    };
    fit();
    const ro = new ResizeObserver(fit);
    ro.observe(lanes);
    return () => ro.disconnect();
  }, [rowsTypical]);

  // ---- name fitting: hide what a bar is too narrow to carry honestly --------
  useLayoutEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    const fit = () => {
      root.querySelectorAll<HTMLElement>('.wl-bar').forEach((el) => {
        const w = el.getBoundingClientRect().width;
        const x = el.closest('.wl-lane')?.classList.contains('is-open') ?? false;
        el.classList.toggle('is-narrow', w < (x ? 78 : 62));
        el.classList.toggle('is-tight', x && w < 118);
        el.classList.toggle('is-noarea', x && w < 200);
      });
    };
    fit();
    // The open transition changes widths over 340ms; fit again once it lands.
    const t = window.setTimeout(fit, 380);
    window.addEventListener('resize', fit);
    return () => {
      window.clearTimeout(t);
      window.removeEventListener('resize', fit);
    };
  }, [shifts, open, axis]);

  // ---- hold to drag ---------------------------------------------------------
  type Drag = {
    id: string;
    el: HTMLElement;
    x0: number;
    y0: number;
    s0: number;
    d0: IsoDate;
    live: boolean;
    moved: boolean;
    axis: 'x' | 'y' | null;
    timer: number;
    target: { date: IsoDate; start: number } | null;
  };
  const drag = useRef<Drag | null>(null);
  const [hint, setHint] = useState<string | null>(null);
  const [over, setOver] = useState<{ date: IsoDate; bad: boolean } | null>(null);
  const suppressClick = useRef(false);

  const vibrate = (pattern: number | number[]) => {
    try {
      navigator.vibrate?.(pattern);
    } catch {
      /* not every browser offers it, and none of them need to */
    }
  };

  const shiftById = useCallback((id: string) => shifts.find((s) => s.id === id), [shifts]);

  const onPointerDown = (e: React.PointerEvent<HTMLElement>) => {
    const el = (e.target as HTMLElement).closest<HTMLElement>('.wl-bar');
    if (!el) return;
    const s = shiftById(el.dataset.id ?? '');
    if (!s || s.locked) return;
    el.setPointerCapture(e.pointerId);
    const d: Drag = {
      id: s.id,
      el,
      x0: e.clientX,
      y0: e.clientY,
      s0: startMin(s),
      d0: s.date,
      live: false,
      moved: false,
      axis: null,
      timer: 0,
      target: null,
    };
    d.timer = window.setTimeout(() => {
      d.live = true;
      el.classList.add('is-lifted');
      vibrate(12);
      setHint('Up or down moves the day. Left or right moves the start.');
    }, HOLD_MS);
    drag.current = d;
  };

  const onPointerMove = (e: React.PointerEvent<HTMLElement>) => {
    const d = drag.current;
    if (!d) return;
    const dx = e.clientX - d.x0;
    const dy = e.clientY - d.y0;
    if (!d.live) {
      // A finger that moves before the hold completes is scrolling, not dragging.
      if (Math.hypot(dx, dy) > 8) {
        window.clearTimeout(d.timer);
        drag.current = null;
      }
      return;
    }
    d.moved = true;
    if (!d.axis && Math.hypot(dx, dy) > AXIS_LOCK_PX) d.axis = Math.abs(dy) > Math.abs(dx) ? 'y' : 'x';
    const s = shiftById(d.id);
    if (!s) return;
    const dur = endMin(s) - startMin(s);
    const trackW = d.el.parentElement?.getBoundingClientRect().width ?? 1;
    const minPerPx = axis.span / trackW;
    const dmin = d.axis === 'x' ? Math.round((dx * minPerPx) / STEP_MIN) * STEP_MIN : 0;
    const ns = Math.max(axis.a0, Math.min(axis.a1 - dur, d.s0 + dmin));

    let date = d.d0;
    if (d.axis === 'y') {
      const lane = document.elementFromPoint(e.clientX, e.clientY)?.closest<HTMLElement>('.wl-lane');
      if (lane?.dataset.date) date = lane.dataset.date;
    }
    const bad = !!s.userId && unavailable.has(s.userId, date);
    setOver({ date, bad });
    d.el.style.left = `${pct(ns)}%`;
    d.el.style.transform = `translateY(${d.axis === 'y' ? dy : 0}px) scale(1.04)`;
    d.target = { date, start: ns };

    // The cost ghost: what this move does to the bill, before the thumb lifts.
    const when = `${DAY_SHORT[dayIndex(date)]} ${fmtTime12(toHhmm(ns))} to ${fmtTime12(toHhmm(ns + dur))}`;
    const r = s.userId ? rates[s.userId] : undefined;
    if (s.userId && r) {
      const hours = shiftMinutes(s.startTime, s.endTime, s.breakMinutes) / 60;
      const diff = hours * r[bucketForDate(date)] - hours * r[bucketForDate(s.date)];
      const money = Math.abs(diff) >= 1 ? ` ${diff > 0 ? '+' : '-'}${fmtMoneyShort(Math.abs(diff))}` : '';
      setHint(bad ? `${people.get(s.userId)?.name ?? 'They'} marked ${DAY_SHORT[dayIndex(date)]} unavailable` : when + money);
    } else {
      setHint(when);
    }
  };

  const endDrag = () => {
    const d = drag.current;
    if (!d) return;
    window.clearTimeout(d.timer);
    d.el.classList.remove('is-lifted');
    d.el.style.transform = '';
    setOver(null);
    setHint(null);
    const s = shiftById(d.id);
    let changed = false;
    if (d.live && d.target && s) {
      const dur = endMin(s) - startMin(s);
      if (d.target.date !== s.date || d.target.start !== startMin(s)) {
        changed = true;
        vibrate(8);
        onMoveShift(s.id, {
          date: d.target.date,
          startTime: toHhmm(d.target.start),
          endTime: toHhmm(Math.min(d.target.start + dur, 24 * 60 - 1)),
        });
      }
    }
    // A drop that changed nothing snaps the bar home; a real move is redrawn
    // by the optimistic patch. Either way the click that follows pointerup
    // must not open the editor.
    if (!changed && s) d.el.style.left = `${pct(startMin(s))}%`;
    suppressClick.current = d.moved;
    drag.current = null;
    window.setTimeout(() => {
      suppressClick.current = false;
    }, 0);
  };

  useEffect(() => () => window.clearTimeout(drag.current?.timer), []);

  // ---- taps -----------------------------------------------------------------
  const onClick = (e: React.MouseEvent<HTMLElement>) => {
    if (suppressClick.current) return;
    const t = e.target as HTMLElement;
    const gap = t.closest<HTMLElement>('.wl-gap');
    const lane = t.closest<HTMLElement>('.wl-lane');
    if (!lane?.dataset.date) return;
    const date = lane.dataset.date;
    if (gap?.dataset.from && gap.dataset.to) {
      onAddShift(date, { startTime: gap.dataset.from as Hhmm, endTime: gap.dataset.to as Hhmm });
      return;
    }
    const bar = t.closest<HTMLElement>('.wl-bar');
    if (bar?.dataset.id) {
      onOpenShift(bar.dataset.id);
      return;
    }
    if (t.closest('.wl-day') || t.closest('.wl-wage')) {
      setOpen((cur) => (cur === date ? null : date));
      vibrate(6);
      return;
    }
    if (t.closest('.wl-track')) {
      if (open !== date) setOpen(date);
      else onAddShift(date);
    }
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLElement>) => {
    const bar = (e.target as HTMLElement).closest<HTMLElement>('.wl-bar');
    if (bar?.dataset.id && (e.key === 'Enter' || e.key === ' ')) {
      e.preventDefault();
      onOpenShift(bar.dataset.id);
    }
  };

  // Hour ticks: every second hour, so labels never collide at 390px.
  const ticks = useMemo(() => {
    const out: number[] = [];
    for (let h = Math.ceil(axis.a0 / 60); h <= Math.floor(axis.a1 / 60); h++) if (h % 2 === 1) out.push(h);
    return out;
  }, [axis]);

  return (
    <div ref={rootRef} className="wl" data-reduced={prefersReducedMotion() ? 'true' : undefined}>
      <div className="wl-axis" aria-hidden="true">
        <span />
        <div className="wl-axis-in">
          {ticks.map((h) => (
            <span key={h} className="wl-tick" style={{ left: `${pct(h * 60)}%` }}>
              {fmtTime12(toHhmm(h * 60)).replace(':00', '')}
            </span>
          ))}
        </div>
        <span />
      </div>

      <div
        className="wl-lanes"
        onClick={onClick}
        onKeyDown={onKeyDown}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
      >
        {weekDates.map((date) => {
          const list = byDate.get(date) ?? [];
          const rows = packRows(list);
          const cov = coverage(list);
          const gaps = cov.filter((c) => c.level === 0);
          const day = totals.byDate[date] ?? { minutes: 0, cost: 0 };
          const state = open === null ? '' : open === date ? 'is-open' : 'is-dim';
          const flag = gaps.length
            ? `${gaps.length} ${gaps.length === 1 ? 'gap' : 'gaps'}`
            : list.some((s) => s.needsCover)
              ? 'needs cover'
              : list.some((s) => !s.userId)
                ? 'unfilled'
                : '';
          const overHere = over?.date === date;
          return (
            <section
              key={date}
              className={`wl-lane ${state} ${date === todayIso ? 'is-today' : ''} ${
                overHere ? (over?.bad ? 'is-bad' : 'is-over') : ''
              }`}
              data-date={date}
              aria-label={`${DAY_SHORT[dayIndex(date)]} ${date.slice(8)}`}
            >
              <button type="button" className="wl-day" aria-expanded={open === date}>
                <span className="wl-dow">{DAY_SHORT[dayIndex(date)]}</span>
                <span className="wl-date tabular">{Number(date.slice(8))}</span>
              </button>

              <div className="wl-track">
                <div className="wl-grid" aria-hidden="true">
                  {ticks.map((h) => (
                    <i key={h} style={{ left: `${pct(h * 60)}%` }} />
                  ))}
                </div>
                <div className="wl-rows">
                  {(rows.length ? rows : [[]]).map((row, i) => (
                    <div className="wl-row" key={i}>
                      {row.map((s) => {
                        const person = s.userId ? people.get(s.userId) : null;
                        const solid = person ? (colours.get(person.userId) ?? 'var(--sp-tpl-slate)') : 'var(--sp-text-faint)';
                        const label = person?.name ?? 'Unfilled';
                        const first = label.split(' ')[0] ?? label;
                        const area = s.areaId ? areaName.get(s.areaId) : null;
                        const brk = s.breakMinutes ? `${s.breakMinutes}m break` : '';
                        return (
                          <div
                            key={s.id}
                            className={`wl-bar ${s.status === 'draft' ? 'is-draft' : ''} ${
                              s.needsCover ? 'is-cover' : ''
                            } ${s.userId ? '' : 'is-open-shift'} ${s.locked ? 'is-locked' : ''}`}
                            data-id={s.id}
                            role="button"
                            tabIndex={0}
                            style={{ left: `${pct(startMin(s))}%`, width: `${pct(endMin(s)) - pct(startMin(s))}%`, ['--wl-solid' as string]: solid }}
                            aria-label={`${label}, ${fmtTime12(s.startTime)} to ${fmtTime12(s.endTime)}${
                              s.needsCover ? ', needs cover' : ''
                            }${s.locked ? ', locked' : ''}${s.status === 'draft' ? ', draft' : ''}`}
                          >
                            <span className="wl-av" aria-hidden="true">
                              {person ? first[0] : '?'}
                            </span>
                            <span className="wl-txt">
                              <span className="wl-nm">{first}</span>
                              <span className="wl-meta">
                                <span className="tabular">
                                  {fmtTime12(s.startTime)} to {fmtTime12(s.endTime)}
                                </span>
                                {area || brk ? <span className="wl-ar">{[area, brk].filter(Boolean).join(', ')}</span> : null}
                              </span>
                            </span>
                            {s.needsCover ? <Hand size={12} className="wl-ic" aria-hidden="true" /> : null}
                            {s.locked ? <Lock size={11} className="wl-ic" aria-hidden="true" /> : null}
                          </div>
                        );
                      })}
                    </div>
                  ))}
                </div>
                <div className="wl-cov" aria-hidden="true">
                  {cov.map((c) => (
                    <i key={c.s} className={`c${c.level}`} style={{ left: `${pct(c.s)}%`, width: `${pct(c.e) - pct(c.s)}%` }} />
                  ))}
                </div>
                {gaps.map((g) => (
                  <button
                    key={g.s}
                    type="button"
                    className="wl-gap"
                    data-from={toHhmm(g.s)}
                    data-to={toHhmm(g.e)}
                    style={{ left: `${pct(g.s)}%`, width: `${pct(g.e) - pct(g.s)}%` }}
                    title={`Nobody on ${fmtTime12(toHhmm(g.s))} to ${fmtTime12(toHhmm(g.e))}|Tap to add a shift that fills it.`}
                    aria-label={`Nobody on ${fmtTime12(toHhmm(g.s))} to ${fmtTime12(toHhmm(g.e))}. Add a shift that fills it.`}
                  />
                ))}
              </div>

              <button type="button" className="wl-wage" aria-label={`${DAY_SHORT[dayIndex(date)]} costs ${fmtMoneyShort(day.cost)}`}>
                <span className="wl-cost tabular">
                  <TweenMoney value={day.cost} short />
                </span>
                <span className="wl-hrs tabular">{day.minutes ? `${(Math.round(day.minutes / 6) / 10).toString().replace(/\.0$/, '')}h` : ''}</span>
                {flag ? <span className="wl-flag">{flag}</span> : null}
              </button>
            </section>
          );
        })}
      </div>

      <div className={`wl-hint ${hint ? 'is-on' : ''}`} role="status" aria-live="polite">
        {hint}
      </div>
    </div>
  );
}
